import PropTypes from 'prop-types';
import { Fragment, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import DownOutlined from '@ant-design/icons/DownOutlined';
import GroupOutlined from '@ant-design/icons/GroupOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';
import { MenuOrientation } from 'src/config';
import useConfig from 'src/hooks/useConfig';

import NavItem from 'src/components/ui/NavItem';
import SimpleBar from 'src/components/ui/SimpleBar';
import Transitions from 'src/components/ui/Transitions';
import NavCollapse from 'src/components/ui/NavCollapse';
import useInterface from 'src/hooks/useInterface';
import { isMenuUrlActive } from 'src/utils/helpers/menuUrl';

import PopperStyled from './styled/PopperStyled';

const NavGroup = ({
	item,
	lastItem,
	remItems,
	lastItemId,
	selectedID,
	setSelectedID,
	setSelectedItems,
	selectedItems,
	setSelectedLevel,
	selectedLevel,
	drawerOpen: drawerOpenProp,
	NavCollapseRP = NavCollapse,
	pathname: pathnameProp,
	search: searchProp,
}) => {
	const { isDrawerOpen } = useInterface();
	const drawerOpen =
		drawerOpenProp === undefined ? isDrawerOpen : drawerOpenProp;
	const theme = useTheme();
	const routerLocation = useLocation();
	const location = {
		pathname: pathnameProp ?? routerLocation.pathname,
		search: searchProp ?? routerLocation.search,
	};

	const { menuOrientation } = useConfig();

	const downLG = useMediaQuery(theme.breakpoints.down('lg'));

	const [anchorEl, setAnchorEl] = useState(null);
	const [currentItem, setCurrentItem] = useState(item);

	const openMini = Boolean(anchorEl);

	useEffect(() => {
		if (lastItem) {
			if (item.id === lastItemId) {
				const localItem = { ...item };
				const elements = remItems.map((ele) => ele.elements);
				localItem.children = elements.flat(1);
				setCurrentItem(localItem);
			} else {
				setCurrentItem(item);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [item, lastItem, downLG]);

	const checkOpenForParent = (child, id) => {
		child.forEach((ele) => {
			if (ele.children?.length) {
				checkOpenForParent(ele.children, currentItem.id);
			}

			if (isMenuUrlActive(ele.link || ele.url, location)) {
				setSelectedID(id);
			}
			if (ele?.isDefaultSelected) {
				setSelectedID(id);
			}
		});
	};

	const checkSelectedOnload = (data) => {
		const childrens = data.children ? data.children : [];
		childrens.forEach((itemCheck) => {
			if (itemCheck?.children?.length) {
				checkOpenForParent(itemCheck.children, currentItem.id);
			}

			if (isMenuUrlActive(itemCheck.link || itemCheck.url, location)) {
				setSelectedID(currentItem.id);
			}

			if (itemCheck?.isDefaultSelected) {
				setSelectedID(currentItem.id);
			}
		});
	};

	useEffect(() => {
		checkSelectedOnload(currentItem);
		if (openMini) setAnchorEl(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname, location.search, currentItem]);

	const handleClick = (event) => {
		if (!openMini) {
			setAnchorEl(event?.currentTarget);
		}
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const isSelected = selectedID === currentItem.id;

	const Icon = currentItem?.icon;
	const itemIcon = currentItem?.icon ? (
		<Icon
			style={{
				fontSize: 20,
				stroke: '1.5',
				color: isSelected
					? theme.palette.primary.main
					: theme.palette.secondary.dark,
			}}
		/>
	) : null;

	const navCollapse = item.children?.map((menuItem, index) => {
		switch (menuItem.type) {
			case 'collapse':
				return (
					<NavCollapseRP
						key={menuItem.id}
						menu={menuItem}
						setSelectedItems={setSelectedItems}
						setSelectedLevel={setSelectedLevel}
						selectedLevel={selectedLevel}
						selectedItems={selectedItems}
						level={1}
						parentId={currentItem.id}
					/>
				);
			case 'item':
				return <NavItem key={menuItem.id} item={menuItem} level={1} />;
			default:
				return (
					<Typography
						key={index}
						variant="h6"
						color="error"
						align="center"
					>
						Fix - Group Collapse or Items
					</Typography>
				);
		}
	});

	const moreItems = remItems.map((itemRem, i) => (
		<Fragment key={i}>
			{itemRem.url ? (
				<NavItem item={itemRem} level={1} />
			) : (
				itemRem.title && (
					<Typography variant="caption" sx={{ pl: 2 }}>
						{itemRem.title} {itemRem.url}
					</Typography>
				)
			)}

			{itemRem?.elements?.map((menu) => {
				switch (menu.type) {
					case 'collapse':
						return (
							<NavCollapseRP
								key={menu.id}
								menu={menu}
								level={1}
								parentId={currentItem.id}
								setSelectedItems={setSelectedItems}
								setSelectedLevel={setSelectedLevel}
								selectedLevel={selectedLevel}
								selectedItems={selectedItems}
							/>
						);
					case 'item':
						return <NavItem key={menu.id} item={menu} level={1} />;
					default:
						return (
							<Typography
								key={menu.id}
								variant="h6"
								color="error"
								align="center"
							>
								Menu Items Error
							</Typography>
						);
				}
			})}
		</Fragment>
	));

	// menu list collapse & items
	const items = currentItem.children?.map((menu) => {
		switch (menu?.type) {
			case 'collapse':
				return (
					<NavCollapseRP
						key={menu.id}
						menu={menu}
						level={1}
						parentId={currentItem.id}
						setSelectedItems={setSelectedItems}
						setSelectedLevel={setSelectedLevel}
						selectedLevel={selectedLevel}
						selectedItems={selectedItems}
					/>
				);
			case 'item':
				return <NavItem key={menu.id} item={menu} level={1} />;
			default:
				return (
					<Typography
						key={menu?.id}
						variant="h6"
						color="error"
						align="center"
					>
						Menu Items Error
					</Typography>
				);
		}
	});

	const popperId = openMini ? `group-pop-${item.id}` : undefined;

	return (
		<>
			{menuOrientation === MenuOrientation.VERTICAL || downLG ? (
				<List
					subheader={
						<>
							{item.title ? (
								drawerOpen && (
									<Box sx={{ pl: 3, mb: 1.5 }}>
										<Typography
											variant="subtitle2"
											color="text.secondary"
										>
											{item.title}
										</Typography>
										{item.caption && (
											<Typography
												variant="caption"
												color="secondary"
											>
												{item.caption}
											</Typography>
										)}
									</Box>
								)
							) : (
								<Divider sx={{ my: 0.5 }} />
							)}
						</>
					}
					sx={{
						mt: drawerOpen && item.title ? 1.5 : 0,
						py: 0,
						zIndex: 0,
					}}
				>
					{navCollapse}
				</List>
			) : (
				<List>
					<ListItemButton
						selected={isSelected}
						sx={{
							p: 1,
							my: 0.5,
							mr: 1,
							display: 'flex',
							alignItems: 'center',
							'&.Mui-selected': { bgcolor: 'transparent' },
						}}
						onMouseEnter={handleClick}
						onClick={handleClick}
						onMouseLeave={handleClose}
						aria-describedby={popperId}
					>
						{itemIcon && (
							<ListItemIcon sx={{ minWidth: 28 }}>
								{currentItem.id === lastItemId ? (
									<GroupOutlined
										style={{ fontSize: 20, stroke: '1.5' }}
									/>
								) : (
									itemIcon
								)}
							</ListItemIcon>
						)}
						<ListItemText
							sx={{ mr: 1 }}
							primary={
								<Typography
									variant="body1"
									color={
										isSelected
											? 'primary.main'
											: 'secondary.dark'
									}
								>
									{currentItem.id === lastItemId ? (
										<FormattedMessage
											id="NavGroup.moreItems"
											defaultMessage="Показать еще"
										/>
									) : (
										currentItem.title
									)}
								</Typography>
							}
						/>
						{openMini ? (
							<DownOutlined
								style={{ fontSize: 16, stroke: '1.5' }}
							/>
						) : (
							<RightOutlined
								style={{ fontSize: 16, stroke: '1.5' }}
							/>
						)}
						{anchorEl && (
							<PopperStyled
								id={popperId}
								open={openMini}
								anchorEl={anchorEl}
								placement="bottom-start"
								style={{ zIndex: 2001 }}
							>
								{({ TransitionProps }) => (
									<Transitions
										in={openMini}
										{...TransitionProps}
									>
										<Paper
											sx={{
												mt: 0.5,
												py: 1.25,
												boxShadow: theme.shadows[8],
												backgroundImage: 'none',
											}}
										>
											<ClickAwayListener
												onClickAway={handleClose}
											>
												<>
													<SimpleBar
														sx={{
															minWidth: 200,
															overflowX: 'hidden',
															overflowY: 'auto',
															maxHeight:
																'calc(100vh - 170px)',
														}}
													>
														{currentItem.id !==
														lastItemId
															? items
															: moreItems}
													</SimpleBar>
												</>
											</ClickAwayListener>
										</Paper>
									</Transitions>
								)}
							</PopperStyled>
						)}
					</ListItemButton>
				</List>
			)}
		</>
	);
};

NavGroup.propTypes = {
	item: PropTypes.any,
	lastItem: PropTypes.number,
	remItems: PropTypes.array,
	lastItemId: PropTypes.string,
	selectedID: PropTypes.oneOfType([PropTypes.any, PropTypes.string]),
	setSelectedID: PropTypes.oneOfType([PropTypes.func, PropTypes.any]),
	setSelectedItems: PropTypes.oneOfType([PropTypes.func, PropTypes.any]),
	selectedItems: PropTypes.oneOfType([PropTypes.any, PropTypes.string]),
	setSelectedLevel: PropTypes.func,
	NavCollapseRP: PropTypes.elementType,
	selectedLevel: PropTypes.number,
	drawerOpen: PropTypes.bool,
	pathname: PropTypes.string,
	search: PropTypes.string,
};

export default NavGroup;
